import { Box } from '@mui/material';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
    chartDataSectionContentSx,
    chartDataSectionDividerSx,
    chartDataSectionHeaderSx,
    chartDataSectionSummaryBannerSx,
    chartDataSectionSx,
    srOnly,
} from '../styles/chartReader.styles';
import {
    ChartDataSectionHeaderSubtitleTypography,
    ChartDataSectionHeaderTitleTypography,
} from '../styles/chartReader.typography';

type ChartDataSectionShellProps = {
    /** First line in the dark navy header band — e.g. "Apple Inc. (AAPL) — Performance of $10,000". */
    title: string;
    /** Second line under the title — e.g. "ETF · Report date: Dec 1, 2025". */
    subtitle: string;
    /** Plain-text sentence. Used verbatim by the NVDA live region and — if
     *  `summaryHTML` is not provided — also rendered in the visual banner.
     *  Changes here trigger a live-region re-announcement. */
    summary: string;
    /** Optional richer-formatting version of `summary` for visual display only
     *  (e.g. with <strong> around key values). The NVDA live region always uses
     *  the plain `summary` so tag names aren't read aloud. Inputs MUST come
     *  from trusted internal formatters — never user-controlled content — since
     *  this is spliced via `dangerouslySetInnerHTML`. */
    summaryHTML?: string;
    /** Bumped by the caller every time the user presses Submit — via
     *  `useSubmittedSelection`'s counter — even when the resulting `summary`
     *  text is unchanged (e.g. re-submitting the same pick). Drives the
     *  polite live region instead of `summary` equality, so a Submit click
     *  never no-ops into silence just because the text happens to match the
     *  last announcement. Required (not defaulted) so every caller has to
     *  wire up `useSubmittedSelection` rather than silently getting a polite
     *  region that can never re-fire after the intro. */
    announcementKey: number;
    /** Plain-text sentence telling the user how to pick a different value —
     *  e.g. "Press Tab to select a different date." Appended to every
     *  Submit-triggered polite announcement (never the mount-time intro,
     *  which already carries its own instructions) so NVDA doesn't strand the
     *  user in silence right after it finishes reading the data — they hear
     *  what to do next in the same utterance. Caller-supplied because the
     *  right noun ("date", "sector", "asset type", ...) depends on which
     *  selector the panel renders as `children`. */
    guidance: string;
    /** Chart-type-specific controls (e.g. Perf10kDataPointSelector). Rendered
     *  in the content slot directly under the header (visually above the
     *  summary banner, though the banner stays first in DOM order). */
    children?: ReactNode;
};

/**
 * Visual shell shared by every chart type. Owns three concerns:
 *   1. Layout — outer card: dark navy header, chart-type-specific content
 *      slot, a decorative divider, then the light-blue summary banner, in
 *      that visual order (via CSS `order`). DOM/reading order stays header →
 *      summary banner → content, unaffected by the reflow.
 *   2. Announcements — two live regions. An assertive region fires once per
 *      mount (i.e. on every Load Chart click, since the shell remounts then)
 *      with the full intro sentence (title, subtitle, summary, and the tab/
 *      Submit/Ctrl+F hints) — self-contained rather than relying on the focus
 *      move in #3 to supply any of it. A polite region announces only
 *      Submit-triggered summary updates from the content slot's controls,
 *      each followed by the caller's `guidance` sentence so the user always
 *      hears how to pick a different value instead of being left in silence
 *      once the data finishes reading.
 *   3. Focus discipline — focus moves to a content-free anchor placed right
 *      before the header on mount, purely to position NVDA's virtual cursor
 *      at the chart as it loads (sighted keyboard Tab order is unaffected —
 *      this element has no natural tab stop). The anchor deliberately has no
 *      text/name: focusing the header directly (it has visible title/
 *      subtitle text) caused NVDA to speak that content on focus *and* via
 *      the assertive announcement in #2 — an audible double-read confirmed
 *      in testing. Skipped if focus is already on something the user is
 *      actively interacting with (e.g. mid Ctrl+F search, or navigating the
 *      search-results listbox) so mounting a chart never steals focus out
 *      from under them. In-panel Submit clicks don't remount the shell, so
 *      focus stays on the Submit button.
 *
 * Chart-type-specific orchestrators (Perf10kChartPanel, etc.) provide title/
 * subtitle/summary computed from their own response shape and render their
 * controls as this component's children. ChartDataSection (the dispatcher)
 * picks which orchestrator runs based on the selected chart type.
 */
export function ChartDataSectionShell({
    title,
    subtitle,
    summary,
    summaryHTML,
    announcementKey,
    guidance,
    children,
}: ChartDataSectionShellProps) {
    const [introAnnouncement, setIntroAnnouncement] = useState('');
    const [summaryAnnouncement, setSummaryAnnouncement] = useState('');
    // Tracks the announcementKey we've already announced. First run (ref ===
    // null) fires the full intro; subsequent runs only re-announce when the
    // key changes — i.e. on every Submit click, regardless of whether the
    // resulting summary text is reference/value-equal to the last one.
    // Comparing the key (not the summary string) is what makes re-submitting
    // an unchanged pick still announce; comparing a boolean flag instead of a
    // value would also break StrictMode — dev-mode's synthetic remount sees
    // the same key already recorded and no-ops instead of clobbering the
    // intro with a spurious re-announcement that NVDA would catch only
    // intermittently.
    const lastAnnouncedKey = useRef<number | null>(null);
    const focusAnchorRef = useRef<HTMLSpanElement>(null);

    // Runs once per mount — i.e. once per Load Chart click, since ChartReaderPage
    // remounts the shell via `key={loadVersion}`. Submit clicks inside the content
    // slot only update local state and never remount, so focus stays put then.
    //
    // Only redirects focus when it's currently somewhere "safe" to redirect from
    // — nowhere in particular, or the Load Chart button that triggered this mount.
    // Deliberately an allowlist, not a denylist of "risky" elements: the set of
    // things a user could be mid-interaction with (a text input, an open Select,
    // a focused row inside the search-results listbox, ...) is open-ended, and a
    // denylist only covers the ones anyone thought to name. If the chart's data
    // resolves while the user has moved on (e.g. pressed Ctrl+F and started
    // typing a new search), mounting must never steal focus/keystrokes from them.
    useEffect(() => {
        const active = document.activeElement;
        const isSafeToRedirect = active === null || active === document.body || active instanceof HTMLButtonElement;
        if (isSafeToRedirect) {
            focusAnchorRef.current?.focus();
        }
    }, []);

    useEffect(() => {
        if (lastAnnouncedKey.current === null) {
            lastAnnouncedKey.current = announcementKey;
            setIntroAnnouncement(
                `${title}. ${subtitle}. ${summary} To find a specific data point, please press tab key, make a selection, then press Submit. You can also press Ctrl + F key to search new security at any time in site`,
            );
            return;
        }
        if (lastAnnouncedKey.current === announcementKey) return;
        lastAnnouncedKey.current = announcementKey;
        // A prior version cleared the region and re-set it 50ms later so NVDA
        // would re-announce even when the new summary is byte-identical to the
        // last one (e.g. re-submitting the same pick unchanged) — but that's a
        // timing race, not a guarantee: NVDA only reliably re-announces when the
        // region's accessible text actually differs from what's currently
        // there, and a fast clear+set can land close enough together that it's
        // missed. Toggling a trailing zero-width space by announcementKey
        // parity guarantees the text differs from the previous render every
        // single time, with no delay and no race — the character is silent to
        // TTS, so it never changes what's actually spoken.
        const ZERO_WIDTH_SPACE = String.fromCharCode(0x200b);
        const zeroWidthToggle = announcementKey % 2 === 0 ? '' : ZERO_WIDTH_SPACE;
        setSummaryAnnouncement(`${summary} ${guidance}${zeroWidthToggle}`);
    }, [summary, guidance, title, subtitle, announcementKey]);

    return (
        <Box sx={chartDataSectionSx}>
            {/* Fires once per mount (Load Chart) — carries title/subtitle/summary
                itself rather than depending on the header focus event below to
                supply them, since the two fire around the same time and either
                can end up interrupting the other. */}
            <div aria-live='assertive' aria-atomic='true' style={srOnly}>
                {introAnnouncement}
            </div>

            {/* Fires only on Submit-triggered summary updates from the content slot.
                Deliberately polite, not assertive — Submit is user-initiated (unlike
                the mount-time intro, which needs to interrupt nothing) and a user who
                re-submits repeatedly shouldn't have each result yank focus/speech away
                from wherever they're reading. */}
            <div aria-live='polite' aria-atomic='true' style={srOnly}>
                {summaryAnnouncement}
            </div>

            {/* Content-free focus anchor, positioned right before the header —
                see class docstring #3. Focusing the header itself (it has visible
                title/subtitle text) caused NVDA to read that content twice: once
                from the focus event, once from the assertive announcement above. */}
            <span ref={focusAnchorRef} tabIndex={-1} style={srOnly} />

            {/* Header — same shape for every chart type. No longer a focus target. */}
            <Box sx={chartDataSectionHeaderSx}>
                <h2 style={{ margin: 0 }}>
                    <ChartDataSectionHeaderTitleTypography>{title}</ChartDataSectionHeaderTitleTypography>
                </h2>
                <ChartDataSectionHeaderSubtitleTypography>{subtitle}</ChartDataSectionHeaderSubtitleTypography>
            </Box>

            {/* Summary banner — visual only; the live regions above handle speech.
                Render the richer HTML version if the caller provided one, otherwise
                fall back to the plain summary text. */}
            <Box sx={chartDataSectionSummaryBannerSx}>
                {summaryHTML ? <span dangerouslySetInnerHTML={{ __html: summaryHTML }} /> : summary}
            </Box>

            {/* Chart-type-specific content slot + its divider — both omitted when
                there's nothing to show (e.g. an empty-data chart type renders no
                selector), so no empty padded strip or stray rule appears above
                the "No data available" banner. */}
            {children ? (
                <>
                    <Box sx={chartDataSectionContentSx}>{children}</Box>
                    <Box aria-hidden='true' data-testid='chart-data-section-divider' sx={chartDataSectionDividerSx} />
                </>
            ) : null}
        </Box>
    );
}
